import { useEffect, useRef, useState } from "react";

/**
 * One shared `<audio>` element for every voice preview button on the page, so
 * starting a new preview always stops whichever one was already playing —
 * there's only ever one element to pause.
 */
export const usePreviewPlayer = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    const stop = () => setPlayingUrl(null);
    audio.addEventListener("ended", stop);
    return () => {
      audio.removeEventListener("ended", stop);
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = (url: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playingUrl === url) {
      audio.pause();
      setPlayingUrl(null);
      return;
    }
    audio.src = url;
    audio.play().then(
      () => setPlayingUrl(url),
      () => setPlayingUrl(null)
    );
  };

  // The play button only exists while its dropdown is open — once it closes
  // (a pick, clicking away, Escape) there's no control left to stop it with,
  // so a still-playing preview would otherwise run on unnoticed.
  const stop = () => {
    audioRef.current?.pause();
    setPlayingUrl(null);
  };

  return { playingUrl, toggle, stop };
};

export default usePreviewPlayer;
